/**
 * Página de Política de Privacidad - Estilo Awwwards
 *
 * Contiene el texto íntegro y verbatim de la Política de Privacidad
 * facilitada por el cliente (documento Word convertido a texto plano), que
 * vive en `locales/es/privacy.json`; las versiones en inglés, francés y
 * portugués son traducciones internas de ese mismo texto.
 * Solo se corrigen artefactos de conversión evidentes (tabulaciones
 * sueltas, espacios dobles); el contenido legal y su redacción se
 * mantienen tal cual, porque es texto legal y no debe reinterpretarse.
 * Única excepción: la errata "repersentación" se corrigió a
 * "representación" (2026-09-29, a petición del equipo).
 *
 * El colectivo "RECETAS" puede enlazarse directamente mediante
 * `/privacidad#recetas`: en ese caso su grupo del acordeón se muestra
 * abierto por defecto y la página hace scroll automático hasta él.
 * El resto de colectivos permanecen cerrados por defecto.
 */

import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import LocalizedLink from '../components/common/LocalizedLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';
import PrivacyAccordionItem from '../components/legal/PrivacyAccordionItem';

gsap.registerPlugin(ScrollTrigger);

/**
 * Identificadores de los colectivos desplegables de la Política de Privacidad,
 * en el orden del documento original.
 *
 * El texto de cada colectivo (título y bloques) vive en el espacio de nombres
 * `privacy` (`groups.<id>`), separado del renderizado (principio de
 * responsabilidad única) para poder traducirlo. Cada colectivo tiene un `title`
 * y una lista de `blocks` que describen, en orden, las preguntas, párrafos y
 * listados del documento original. El `id` se usa como ancla y para los
 * atributos ARIA del acordeón, por eso no se traduce.
 *
 * Tipos de bloque soportados por `renderPolicyBlocks`:
 *  - { type: 'question', text }  -> subtítulo en negrita
 *  - { type: 'paragraph', text } -> párrafo de texto
 *  - { type: 'list', items }     -> lista con viñetas
 */
const GROUP_IDS = [
  'contactos-web',
  'clientes',
  'recetas',
  'encuestas-calidad',
  'proveedores',
  'redes-sociales',
  'transparencia',
  'informacion-adicional',
];

/**
 * Renderiza la lista de bloques (preguntas, párrafos y listados) de un
 * colectivo de la Política de Privacidad, manteniendo la separación
 * entre los datos (`privacyGroups`) y su presentación.
 *
 * @param {Array<Object>} blocks - Bloques de contenido del colectivo
 * @returns {JSX.Element} Fragmento con el contenido renderizado
 */
const renderPolicyBlocks = (blocks) => (
  <div className="space-y-4">
    {blocks.map((block, index) => {
      // eslint-disable-next-line react/no-array-index-key
      const key = `${block.type}-${index}`;
      if (block.type === 'question') {
        return (
          <h4 key={key} className="font-baskerville text-base md:text-lg font-bold text-intercasa-dark pt-2">
            {block.text}
          </h4>
        );
      }
      if (block.type === 'list') {
        return (
          <ul key={key} className="space-y-2 text-intercasa-gray text-base leading-relaxed">
            {block.items.map((item) => (
              <li key={item} className="flex items-start">
                <span className="text-intercasa-red mr-3 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      }
      return (
        <p key={key} className="text-intercasa-gray text-base leading-relaxed">
          {block.text}
        </p>
      );
    })}
  </div>
);

/**
 * Página de Política de Privacidad con estilo Awwwards
 * @returns {JSX.Element} Página de política de privacidad inmersiva
 */
const PrivacyPage = () => {
  const { t } = useTranslation('privacy');
  const heroRef = useRef(null);
  const location = useLocation();
  // El colectivo RECETAS solo se abre por defecto cuando se accede con el
  // ancla `#recetas` (p.ej. desde el formulario de envío de recetas).
  const recetasOpenByHash = location.hash === '#recetas';

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  // Si se llega con el ancla #recetas, hace scroll hasta el bloque una vez
  // que el acordeón se ha expandido y el `ScrollToTop` global (que fuerza
  // scroll(0,0) al montar la ruta) ha terminado sus reintentos.
  useEffect(() => {
    if (location.hash !== '#recetas') return undefined;

    const timer = setTimeout(() => {
      const target = document.getElementById('recetas');
      if (!target) return;

      if (window.__lenis) {
        window.__lenis.scrollTo(target, { offset: -120 });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pt-28 md:pt-32 pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />

        {/* Imagen en patrón repetido (tile) */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />

        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              <Trans
                i18nKey="hero.title"
                ns="privacy"
                components={{ hl: <span className="text-intercasa-yellow" /> }}
              />
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Introducción */}
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('intro')}
                </p>
              </div>
            </AnimatedSection>

            {/* Responsable del tratamiento */}
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('controller.title')}
                </h2>
                <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red space-y-2 text-intercasa-gray text-lg">
                  <p>{t('controller.name')}</p>
                  <p>{t('controller.address')}</p>
                  <p>{t('controller.cif')}</p>
                  <p>
                    {t('controller.phone')}{' '}
                    <a href="tel:+34928732001" className="text-intercasa-red underline hover:no-underline">
                      +34 928 732 001
                    </a>
                  </p>
                  <p>
                    {t('controller.email')}{' '}
                    <a href="mailto:info@intercasacanarias.es" className="text-intercasa-red underline hover:no-underline">
                      info@intercasacanarias.es
                    </a>
                  </p>
                  <p>
                    {t('controller.dpo')}{' '}
                    <a href="mailto:dpo@livvohotels.com" className="text-intercasa-red underline hover:no-underline">
                      dpo@livvohotels.com
                    </a>
                  </p>
                </div>
                <p className="text-intercasa-gray text-lg leading-relaxed mt-6">
                  {t('controller.contactAnyWay')}
                </p>
                <p className="text-intercasa-gray text-lg leading-relaxed mt-4">
                  {t('controller.changes')}
                </p>
              </div>
            </AnimatedSection>

            {/* Colectivos: acordeón */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('groupsSection.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-8">
                  {t('groupsSection.intro')}
                </p>
                <div className="space-y-4">
                  {GROUP_IDS.map((groupId) => {
                    const isRecetas = groupId === 'recetas';
                    return (
                      <PrivacyAccordionItem
                        // El grupo de RECETAS se remonta cuando cambia el ancla de
                        // la URL, de forma que recoja su nuevo `defaultOpen`.
                        key={isRecetas ? `recetas-${recetasOpenByHash}` : groupId}
                        id={groupId}
                        title={t(`groups.${groupId}.title`)}
                        defaultOpen={isRecetas && recetasOpenByHash}
                      >
                        {renderPolicyBlocks(t(`groups.${groupId}.blocks`, { returnObjects: true }))}
                      </PrivacyAccordionItem>
                    );
                  })}
                </div>
              </div>
            </AnimatedSection>

            {/* Contacto */}
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('contact.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-8">
                  {t('contact.text')}
                </p>
                <LocalizedLink
                  to="/contacto"
                  className="inline-flex items-center gap-2 bg-intercasa-red hover:bg-intercasa-red/90 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  {t('contact.cta')}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </LocalizedLink>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default PrivacyPage;
